import { useParams, Link } from 'react-router-dom';
import {
  Card, CardHeader, CardBody, StatusBadge, Loading, ErrorNotice,
} from '../../components/ui/index.js';
import { useResource } from '../../hooks/useApi.js';
import { formatMoney, formatExactTimestamp } from '../../lib/format.js';
import { PageHero } from '../../components/PageHero.jsx';
import { GatewayDataCard } from '../../components/GatewayDataCard.jsx';

const SOURCE_LABEL = { api: 'API', pos: 'Counter (POS)', link: 'Payment link', dashboard: 'Dashboard' };

/**
 * One payment, for an operator investigating it — usually because a gateway
 * has asked what was sent. The details carry no customer contact; the gateway
 * card below shows exactly what went to the gateway, which can include the
 * customer's email, because that is the question being answered.
 */
export function AdminPaymentDetailPage() {
  const { id } = useParams();
  const { data: payment, error, loading, refetch } = useResource(`/admin/payments/${id}`);

  if (loading) return <Loading label="Loading payment…" />;
  if (error) {
    return (
      <>
        <PageHero title="Payment" backTo="/admin/payments" />
        <ErrorNotice error={error} onRetry={refetch} title="Could not load this payment" />
      </>
    );
  }

  const details = [
    ['Payment ID', <span className="font-mono text-xs">{payment.id}</span>],
    ['Organisation', (
      <Link to={`/admin/merchants/${payment.merchantId}`} className="text-accent-ink hover:underline">
        {payment.merchantName ?? payment.merchantId}
      </Link>
    )],
    ['Order ID', payment.orderId ?? '—'],
    ['Description', payment.description || '—'],
    ['Amount', formatMoney(payment.amount, payment.currency)],
    ['Refunded', payment.amountRefunded > 0 ? formatMoney(payment.amountRefunded, payment.currency) : '—'],
    ['Method', payment.method?.label ?? '—'],
    ['Taken through', SOURCE_LABEL[payment.source] ?? payment.source],
    ['Provider transaction', <span className="font-mono text-xs">{payment.providerTransactionId ?? '—'}</span>],
    ['Environment', <StatusBadge status={payment.environment} />],
    ['Created', formatExactTimestamp(payment.createdAt)],
    ['Succeeded', payment.succeededAt ? formatExactTimestamp(payment.succeededAt) : '—'],
  ];

  return (
    <>
      <PageHero
        backTo="/admin/payments"
        chip="Platform"
        title={formatMoney(payment.amount, payment.currency)}
        blurb={payment.merchantName ?? undefined}
        actions={
          <>
            {payment.needsReconciliation && <span className="chip-warning">Needs reconciliation</span>}
            <StatusBadge status={payment.status} />
          </>
        }
      />

      {payment.failure && (
        <ErrorNotice
          error={{ message: payment.failure.message, code: payment.failure.code }}
          title="This payment failed"
          className="mb-6"
        />
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader title="Details" />
          <CardBody>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {details.map(([term, value]) => (
                <div key={term} className="min-w-0">
                  <dt className="text-xs font-medium uppercase tracking-wide text-muted">{term}</dt>
                  <dd className="mt-1 break-words text-sm text-ink">{value}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>

        {/* Nothing was sent anywhere for a payment the merchant collected themselves. */}
        {payment.method?.kind === 'provider' && (
          <GatewayDataCard path={`/admin/payments/${payment.id}/gateway-data`} />
        )}
      </div>
    </>
  );
}

export default AdminPaymentDetailPage;
